import React from 'react';
import { motion } from 'motion/react';
import { Helmet } from 'react-helmet-async';
import { getCanonicalUrl } from '@/lib/publicBaseUrl';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Link } from 'react-router-dom';
import { CheckCircle2, TrendingUp, ShieldCheck, Target, ArrowRight } from 'lucide-react';
import { CommitmentLadder } from '@/components/shared/CommitmentLadder';
import { LazyImage } from '@/components/ui/LazyImage';

export function ExecutiveCoachingPage() {
  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground">
      <Helmet>
        <title>Executive Coaching | FIVE LENS Enneagram</title>
        <meta name="description" content="Transform your leadership approach with targeted executive coaching using the FIVE LENS multidimensional framework." />
        <link rel="canonical" href={getCanonicalUrl('/executive-coaching')} />
      </Helmet>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 mb-8">
        <Breadcrumb items={[{ label: 'Executive Coaching' }]} />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-rule-3 mb-4 block">1-on-1 Development</span>
            <h1 className="text-4xl lg:text-5xl font-serif text-ink mb-6 leading-tight">
              Executive Coaching
            </h1>
            <p className="text-xl text-ink/80 mb-8 leading-relaxed font-light">
              Elevate your leadership impact through deeply personalized coaching. The FIVE LENS framework moves beyond standard executive coaching by uncovering the root drivers of behavior, enabling rapid and sustainable transformation.
            </p>
            <div className="space-y-4 mb-8">
              {[
                'Identify and overcome deeply ingrained blind spots',
                'Develop advanced emotional regulation and resilience',
                'Navigate systemic organizational complexity with clarity',
                'Align your core values with your leadership style'
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-rule-3 shrink-0 mt-0.5" />
                  <span className="text-ink/80">{item}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex justify-center items-center gap-2 bg-ink text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-rule-3 transition-colors"
              >
                Book a Strategy Call <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white p-2 border border-ground-deep relative"
          >
            <LazyImage 
              src="/five_lens_executive_coaching_web.webp" 
              alt="Executive Coaching Session" 
              className="w-full h-full object-cover aspect-[4/3]"
            />
          </motion.div>
        </div>
      </div>

      <section className="bg-white py-24 border-y border-ground-deep">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-serif text-ink mb-6">The FIVE LENS Difference</h2>
            <p className="text-lg text-ink/70/80 font-light leading-relaxed">
              Traditional coaching often focuses purely on skill acquisition or behavioral modification. Our approach uses the five distinct lenses to decode the complex psychological systems driving your decisions, unlocking a profound level of self-mastery.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 border border-ground-deep hover:shadow-md hover:shadow-ink/5 hover:border-ink hover:-translate-y-1 transition-all group">
              <Target className="w-8 h-8 text-rule-3 mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-serif text-ink mb-4">Precision Diagnostics</h3>
              <p className="text-ink/70/70 leading-relaxed text-sm">
                We begin with a comprehensive FIVE LENS assessment, instantly pinpointing core friction areas, resilience markers, and energy imbalances.
              </p>
            </div>
            <div className="p-8 border border-ground-deep hover:shadow-md hover:shadow-ink/5 hover:border-ink hover:-translate-y-1 transition-all group">
              <ShieldCheck className="w-8 h-8 text-rule-3 mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-serif text-ink mb-4">Psychological Safety</h3>
              <p className="text-ink/70/70 leading-relaxed text-sm">
                Our certified coaches create a secure, confidential environment where you can safely explore vulnerabilities and challenge long-held assumptions.
              </p>
            </div>
            <div className="p-8 border border-ground-deep hover:shadow-md hover:shadow-ink/5 hover:border-ink hover:-translate-y-1 transition-all group">
              <TrendingUp className="w-8 h-8 text-rule-3 mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-serif text-ink mb-4">Sustainable Impact</h3>
              <p className="text-ink/70/70 leading-relaxed text-sm">
                Instead of temporary fixes, we build systemic resilience and emotional agility that endures long after the coaching engagement concludes.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-ground border-b border-ground-deep">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-serif text-ink mb-8">Who is this for?</h2>
          <div className="grid sm:grid-cols-2 gap-8 text-left">
            <div className="bg-white p-8 border border-ground-deep">
              <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-3">C-Suite & Founders</h4>
              <p className="text-sm text-ink/70/80 leading-relaxed">
                Navigate the unique pressures of top-tier leadership. Develop the strategic presence and emotional regulation required to lead through uncertainty.
              </p>
            </div>
            <div className="bg-white p-8 border border-ground-deep">
              <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-3">High-Potential Leaders</h4>
              <p className="text-sm text-ink/70/80 leading-relaxed">
                Accelerate your transition into senior roles by closing the gap between technical expertise and emotional intelligence.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CommitmentLadder context="general" />
    </main>
  );
}
